import debounce from "lodash/debounce"
import { useEffect, useMemo, useState } from "react"

import { cn } from "@/lib/utils"
import { IconSearch, IconSearchOff } from "@tabler/icons-react"

import { Button } from "../../Button"
import { Input } from "../../Input"
import { Tooltip } from "../../Tooltip"
import { LE_TableInstance } from "../types"

const TOGGLE_GLOBAL_FILTER_BUTTON_ID_SUFFIX = "toggle-global-filter-button"

function GlobalFilterInput<T extends Record<string, any> = object>({
  table,
  visible,
  className,
}: {
  table: LE_TableInstance<T>
  visible?: true
  className?: string
}) {
  const {
    refs: { globalSearchInputRef },
  } = table
  const { showFilter, globalFilter } = table.getState()
  const [value, setValue] = useState(globalFilter)

  const debounceValue =
    table.debounceGlobalSearch === false
      ? 0
      : typeof table.debounceGlobalSearch === "number"
        ? table.debounceGlobalSearch
        : 500

  // using `useMemo` here so eslint can pick up the relevant dependencies
  const handleSetGlobalFilter = useMemo(
    () => debounce((value: string) => table.setGlobalFilter(value), debounceValue),
    [debounceValue, table],
  )

  useEffect(() => handleSetGlobalFilter(value), [value, handleSetGlobalFilter])

  return (
    <Input
      ref={globalSearchInputRef}
      placeholder="Search"
      value={value}
      onChange={(e) => setValue(e.target.value)}
      rootClassName={cn(
        "-my-px h-fit rounded-full",
        { hidden: !(visible || showFilter) },
        className,
      )}
      className={cn("py-0.5")}
    />
  )
}

function ToggleButton<T extends Record<string, any> = object>({
  table,
}: {
  table: LE_TableInstance<T>
}) {
  const {
    refs: { globalSearchInputRef },
    setShowFilter,
    resetGlobalFilter,
  } = table

  const { showFilter } = table.getState()

  return (
    <Tooltip content="Toggle search" dark>
      <Button
        id={table.getElementId(TOGGLE_GLOBAL_FILTER_BUTTON_ID_SUFFIX)}
        variant="neutral"
        iconOnly
        icon={showFilter ? IconSearchOff : IconSearch}
        onClick={() => {
          setShowFilter((v) => {
            if (v) resetGlobalFilter(false)
            return !v
          })
          setTimeout(() => globalSearchInputRef.current?.focus(), 0)
        }}
        aria-label="toggle search"
      />
    </Tooltip>
  )
}

function ClearButton<T extends Record<string, any> = object>({
  table,
}: {
  table: LE_TableInstance<T>
}) {
  const {
    refs: { globalSearchInputRef },
    resetGlobalFilter,
  } = table

  return (
    <Tooltip content="Clear search" dark>
      <Button
        id={table.getElementId(TOGGLE_GLOBAL_FILTER_BUTTON_ID_SUFFIX)}
        variant="neutral"
        iconOnly
        icon={IconSearchOff}
        onClick={() => {
          resetGlobalFilter(false)
          setTimeout(() => globalSearchInputRef.current?.focus(), 0)
        }}
        aria-label="clear search"
      />
    </Tooltip>
  )
}

export const GlobalFilter = {
  Input: GlobalFilterInput,
  ToggleButton,
  ClearButton,
}
